import { ImportType } from "@medapro-types/Imports/import-enum";
import { Guid } from "@util/guid";
import api from "./api";
import endpoints from "./endpoints";
import { ImportProjectState, SourceStructureSaveModel } from "@medapro-types/Imports/store/imports";
import { CSVConnectionModel } from "@medapro-types/common/common";
import { TargetMappingNode } from "@medapro-types/Imports/components/MappingCard/TargetStructure";
import { ImportProgressState } from "@medapro-types/Imports/components/ProgressImport";
import { useSanitize } from "@common/composable/useSanitize";
import { ExternalSourceConnection } from "@medapro-types/Imports/Import";
const { sanitizeFilename } = useSanitize();

export default {
  getAllImportProjects: async function (type: ImportType | null) {
    const response = await api.get(endpoints.import.getAll, { params: { type: type } });
    return response.data;
  },
  startImport: async function (importId: string) {
    console.log("Starting import with id ", importId);
    const response = await api.post(endpoints.import.start, {}, { params: { importId: importId } });
    return response.data;
  },
  getSourceStructure: async function (importId: string) {
    const response = await api.get(endpoints.import.source.get, { params: { importId: importId } });
    return response.data;
  },
  getTargetStructure: async function (importLanguageCode: string) {
    const response = await api.get(endpoints.import.getTargetStructure, {
      params: { importLanguageCode: importLanguageCode }
    });
    return response.data;
  },
  save: async function (importProjectState: ImportProjectState) {
    const response = await api.post(endpoints.import.save, importProjectState);
    return response.data;
  },
  delete: async function (importId: string[]) {
    if (importId && importId.length !== 0) {
      const response = await api.post(endpoints.import.delete, importId);
      return response.data;
    }
  },
  uploadSourceFile: async function (
    importId: string,
    importType: ImportType | null,
    csvConnectionModel: CSVConnectionModel | null,
    file: File,
    signal: AbortSignal | null = null
  ) {
    const formData = new FormData();
    if (csvConnectionModel) {
      formData.append("csvConnectionModel", JSON.stringify(csvConnectionModel));
    }
    formData.append("file", file, sanitizeFilename(file.name));
    return api
      .post(endpoints.import.source.upload, formData, {
        params: { importId, importType },
        signal: signal,
        headers: { "Content-Type": "multipart/form-data" }
      })
      .then((x) => Promise.resolve(x.data));
  },
  saveSourceStructure: async function (sourceStructureSaveModel: SourceStructureSaveModel, csvConnectionModel: CSVConnectionModel | null) {
    const params = { ...sourceStructureSaveModel };
    return api.post(endpoints.import.source.save, csvConnectionModel, { params }).then((x) => Promise.resolve(x.data));
  },
  getMappings: async function (importId: string) {
    const response = await api.get(endpoints.import.mappings.get, {
      params: { importId: importId }
    });
    return response.data;
  },
  saveMappings: async function (importId: string, mappings: TargetMappingNode[]) {
    return api.post(endpoints.import.mappings.save, mappings, { params: { importId } }).then((x) => Promise.resolve(x.data));
  },
  deleteMappings: async function (mappingIds: string[]) {
    return api.post(endpoints.import.mappings.delete, mappingIds).then((x) => Promise.resolve(x.data));
  },
  deleteSourceFile: async function (importId: string) {
    if (!Guid.isEmpty(importId)) {
      const response = await api.post(endpoints.import.source.delete, {}, { params: { importId } });
      return response.data;
    }
  },
  reloadSourceFile: async function (importId: string, signal: AbortSignal | null = null) {
    if (!Guid.isEmpty(importId)) {
      const response = await api.post(endpoints.import.source.reload, {}, { params: { importId }, signal: signal });
      return response.data;
    }
  },
  getProgressInfo: async function (language: string, importRunId: string): Promise<ImportProgressState> {
    const response = await api.get(endpoints.import.getProgress, { params: { importRunId: importRunId, language: language } });
    return response.data;
  },
  cancelImport: async function (importRunId: string) {
    const response = await api.post(endpoints.import.cancel, {}, { params: { importRunId: importRunId } });
    return response.data;
  },
  copyProject: async function (sourceImportProjectId: string, language: string) {
    const response = await api.post(endpoints.import.copy, {}, { params: { sourceImportProjectId: sourceImportProjectId, language: language } });
    return response.data;
  },
  getLastImport: async function (importId: string) {
    const response = await api.get(endpoints.import.getLastImport, { params: { importId } });
    return response.data;
  },
  isLastImportEnabled: async function (importId: string) {
    const response = await api.get(endpoints.import.getLastImportEnabledStatus, { params: { importId } });
    return response.data;
  },
  getExternalSourceConnections: async function (): Promise<ExternalSourceConnection[]> {
    const response = await api.get(endpoints.import.getExternalSourceConnections);
    return Object.entries(response.data).map(([key, value]) => ({
      connectionId: key,
      connectionName: value as string
    }));
  },
  getSourceStructureForExternalSourceImport: async function (connectionId: string): Promise<ExternalSourceConnection[]> {
    const response = await api.get(endpoints.import.getSourceStructureForExternalSourceImport, { params: { connectionId } });
    return response.data;
  },
  getFilePreview: async function (importId: string, importSourceTableName: string, signal: AbortSignal) {
    const response = await api.get(endpoints.import.source.filePreview, { params: { importId, importSourceTableName }, signal });
    return response.data;
  }
};
